import { useId, useState } from "react";
import type { VerificationAnswer } from "@ondo/shared";

import { ANSWERS, bulkSummary } from "./verifications.model.ts";
import {
  ANSWER_COMPOSERS,
  ANSWER_LABELS,
  VERIFICATION_COPY,
} from "./verifications.constants.ts";

type Props = {
  selected: string[];
  titles: string[];
  busy: boolean;
  onSend: (body: { answer: VerificationAnswer; reason: string }) => void;
  onClear: () => void;
};

/**
 * Bulk answering, behind a sheet that says what is about to be signed.
 *
 * The sheet is not a courtesy. Confirming is putting your name behind somebody
 * else's claim, and a control that did twenty at once without naming them would
 * be a control nobody read twenty claims before using — which is the same
 * argument `MAX_BULK` makes with a number.
 */
export function BulkActionBar({ selected, titles, busy, onSend, onClear }: Props) {
  const [answer, setAnswer] = useState<VerificationAnswer | null>(null);
  const [reason, setReason] = useState("");
  const fieldId = useId();

  if (selected.length === 0) return null;

  const composer = answer ? ANSWER_COMPOSERS[answer] : null;

  return (
    <div className="verifications__bulk" role="region" aria-label="Bulk actions">
      <p className="verifications__bulk-count">
        {selected.length} selected
        <button type="button" className="verifications__link" onClick={onClear}>
          Clear
        </button>
      </p>

      {answer === null ? (
        <div className="verifications__buttons">
          {ANSWERS.map((option) => (
            <button
              key={option}
              type="button"
              className={`verifications__button verifications__button--${option}`}
              onClick={() => {
                setReason("");
                setAnswer(option);
              }}
            >
              {ANSWER_LABELS[option]}
            </button>
          ))}
        </div>
      ) : (
        <div className="verifications__composer">
          <p className="verifications__bulk-summary">
            {bulkSummary(answer, selected.length)}
          </p>
          <p>{VERIFICATION_COPY.bulkConfirm}</p>
          <ul className="verifications__bulk-list">
            {titles.map((title, index) => (
              <li key={`${title}-${String(index)}`}>{title}</li>
            ))}
          </ul>

          {/* One reason for all of them, which the check constraint requires
              on a dispute wherever it is recorded. */}
          <label htmlFor={fieldId}>
            {answer === "dispute" ? VERIFICATION_COPY.bulkReasonLabel : composer?.label}
          </label>
          <textarea
            id={fieldId}
            rows={3}
            value={reason}
            placeholder={composer?.placeholder}
            onChange={(event) => setReason(event.target.value)}
          />

          <div className="verifications__buttons">
            <button
              type="button"
              className="verifications__button"
              onClick={() => setAnswer(null)}
            >
              Back
            </button>
            <button
              type="button"
              className="verifications__button verifications__button--send"
              disabled={busy || (answer === "dispute" && reason.trim().length === 0)}
              onClick={() => {
                onSend({ answer, reason: reason.trim() });
                setAnswer(null);
              }}
            >
              {ANSWER_LABELS[answer]}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
